import { api_without_cancellation } from "@/api/api";

const SETTINGS_ENDPOINT = "/api/firmsetting-page/fax-settings/";
const VARIABLES_ENDPOINT = "/api/firmsetting-page/fax-settings/variables/";
const FIRM_EMAILS_ENDPOINT = "/api/firmsetting-page/firm-email/";

const unwrapData = (response) => response?.data?.data ?? response?.data;

const normalizeRequestError = (error, fallback) => {
  const responseData = error?.response?.data;
  const message =
    responseData?.error ||
    responseData?.detail ||
    responseData?.message ||
    error?.message ||
    fallback;
  const normalizedError = new Error(message);
  normalizedError.response = error?.response;
  return normalizedError;
};

export const getFaxSettings = async () => {
  try {
    const response = await api_without_cancellation.get(SETTINGS_ENDPOINT);
    return unwrapData(response);
  } catch (error) {
    throw normalizeRequestError(error, "Unable to load the firm's fax settings.");
  }
};

export const getFaxTemplateVariables = async () => {
  try {
    const response = await api_without_cancellation.get(VARIABLES_ENDPOINT);
    return unwrapData(response)?.variables || [];
  } catch (error) {
    throw normalizeRequestError(error, "Unable to load the cover sheet variables.");
  }
};

const getFirmEmailAddress = (account) =>
  account?.protocol_settings?.email_username ||
  account?.protocol_settings?.email_address ||
  account?.gmail_settings?.email_address ||
  account?.outlook_settings?.email_address ||
  "";

export const getConnectedFirmEmails = async () => {
  try {
    const response = await api_without_cancellation.get(FIRM_EMAILS_ENDPOINT);
    const responseData = unwrapData(response) || {};
    const seenEmails = new Set();

    return Object.entries(responseData)
      .filter(([key, account]) => /^firm_email_\d+$/.test(key) && account)
      .map(([key, account]) => ({
        id: account.id || key,
        email: getFirmEmailAddress(account).trim(),
        settingsType: account.settings_type || "Connected email",
        accountType: account.connected_account_type || "Firm Account",
      }))
      .filter(({ email }) => {
        const normalizedEmail = email.toLowerCase();
        if (!normalizedEmail || seenEmails.has(normalizedEmail)) return false;
        seenEmails.add(normalizedEmail);
        return true;
      });
  } catch (error) {
    throw normalizeRequestError(error, "Unable to load the firm's connected email accounts.");
  }
};

export const patchFaxSettings = async (payload) => {
  try {
    const response = await api_without_cancellation.patch(SETTINGS_ENDPOINT, payload, {
      headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    return unwrapData(response);
  } catch (error) {
    throw normalizeRequestError(error, "Unable to save the firm's fax settings.");
  }
};
